import React from 'react';
import {
  Box,
  Flex,
  IconButton,
  Portal,
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverArrow,
  PopoverCloseButton,
  Textarea,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/pro-regular-svg-icons';
import moment from 'moment';
import { findLastIndex } from 'lodash';

import { IMessage } from 'types';
import { Message, MessagesButton, UnreadBadge } from 'shared-components';

export enum TabIndex {
  'Student',
  'Agent',
  'Secure'
}

interface IProps {
  messages: Array<IMessage>;
  onSubmitMessage: (message: IMessage) => void;
  markMessagesRead: (markMessagesRead: Array<string>) => void;
  userName: string;
  studentAutomatedChatReply: string;
  visaAssistance: boolean;
  isAgent: boolean;
  hasAgentMessages: boolean;
  hasSecureMessages: boolean;
  hasImmigrationProviders: boolean;
  isSamlEnabled?: boolean;
  onLoadMessages?: () => void;
  unreadMessages: number;
}

interface IState {
  isMinimised: boolean;
  messages: Array<IMessage>;
  message: string;
  isSendToStudent: boolean;
  unreadMessagesCountFromUni: number; // unread messages for student from uni
  unreadMessagesAgentToStudent: number; // unread messages for student from agent
  unreadMessagesImmigrationProviderToStudent: number; // unread messages for student from immigration provider
  unreadMessagesStudentSecure: number; // unread messages for student from uni secure
  shouldNotifyUserOfResponseTime: boolean;
  isVisibleTooltip: boolean;
  tooltipIndex: number | null;
  tabIndex: TabIndex;
  tabIndexes: Map<number, string>;
  visaAssistance: boolean;
  isAgent: boolean;
  hasAgentMessages: boolean;
  hasSecureMessages: boolean;
  hasImmigrationProviders: boolean;
}

export class MessageInbox extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      isMinimised: true,
      messages: props.messages ? props.messages : [],
      message: '',
      isSendToStudent: true,
      unreadMessagesCountFromUni: this.getUnreadMessagesFromUni(props.messages).length ?? 0,
      unreadMessagesAgentToStudent: this.getUnreadMessagesFromAgent(props.messages).length ?? 0,
      unreadMessagesStudentSecure: this.getUnreadMessagesFromSecure(props.messages).length ?? 0,
      unreadMessagesImmigrationProviderToStudent:
        this.getUnreadMessagesFromImmigrationProvider(props.messages).length ?? 0,
      shouldNotifyUserOfResponseTime: true,
      isVisibleTooltip: false,
      tooltipIndex: null,
      tabIndex: 0,
      visaAssistance: props.visaAssistance ? props.visaAssistance : false,
      isAgent: props.isAgent ? props.isAgent : false,
      tabIndexes: this.getTabIndexes(props.hasAgentMessages, props.hasSecureMessages, props.hasImmigrationProviders),
      hasAgentMessages: props.hasAgentMessages || false,
      hasSecureMessages: props.hasSecureMessages || false,
      hasImmigrationProviders: props.hasImmigrationProviders || false
    };
  }

  UNSAFE_componentWillReceiveProps(props: IProps) {
    if (props.messages || props.isAgent || props.visaAssistance || props.hasImmigrationProviders) {
      let messagesUpdate = {};
      if (props.messages) {
        this.handleAutomatedResponse(props.messages);
        messagesUpdate = {
          messages: props.messages,
          unreadMessagesCountFromUni: this.getUnreadMessagesFromUni(props.messages).length ?? 0,
          unreadMessagesAgentToStudent: this.getUnreadMessagesFromAgent(props.messages).length ?? 0,
          unreadMessagesStudentSecure: this.getUnreadMessagesFromSecure(props.messages).length ?? 0,
          unreadMessagesImmigrationProviderToStudent:
            this.getUnreadMessagesFromImmigrationProvider(props.messages).length ?? 0,
          tabIndexes: this.getTabIndexes(
            props.hasAgentMessages,
            props.hasSecureMessages,
            props.hasImmigrationProviders
          ),
          hasAgentMessages: props.hasAgentMessages || false,
          hasSecureMessages: props.hasSecureMessages || false,
          hasImmigrationProviders: props.hasImmigrationProviders || false
        };
      }

      this.setState(
        {
          ...messagesUpdate,
          visaAssistance: props.visaAssistance,
          isAgent: props.isAgent
        }
      );
    }
  }

  componentDidMount(): void {
    this.handleAutomatedResponse(this.state.messages);
    this.props.onLoadMessages();
  }

  getTabIndexes = (
    hasAgentMessages: boolean,
    hasSecureMessages: boolean,
    hasImmigrationProviders: boolean
  ): Map<number, string> => {
    const tabIndexes = [[0, 'student']];

    if (hasAgentMessages) {
      tabIndexes.push([tabIndexes.length, 'agent']);
    }

    if (hasSecureMessages) {
      tabIndexes.push([tabIndexes.length, 'secure']);
    }

    if (hasImmigrationProviders) {
      tabIndexes.push([tabIndexes.length, 'immigrationProvider']);
    }

    return new Map(tabIndexes as any);
  };

  getUnreadMessagesFromUni = (messages: Array<IMessage>): Array<IMessage> => {
    return this.filterMessagesByUni(messages).filter(
      message => message.messageTo === 'student' && message?.read !== true
    );
  };

  getUnreadMessagesFromAgent = (messages: Array<IMessage>): Array<IMessage> => {
    return this.filterMessagesByAgent(messages).filter(
      message => message.messageTo === 'student' && message?.read !== true
    );
  };
  getUnreadMessagesFromImmigrationProvider = (messages: Array<IMessage>): Array<IMessage> => {
    return this.filterMessagesByImmigrationProvider(messages).filter(
      message => message.messageTo === 'student' && message?.read !== true
    );
  };

  getUnreadMessagesFromSecure = (messages: Array<IMessage>): Array<IMessage> => {
    return this.filterMessagesBySecure(messages).filter(
      message => message.messageTo === 'student' && message?.read !== true
    );
  };

  onClickMinimisedInbox = () => {
    if(this.props.onLoadMessages) this.props.onLoadMessages();
  };

  onClickErrorIcon = (index: number) => {
    this.setState({
      isVisibleTooltip: !this.state.isVisibleTooltip,
      tooltipIndex: index
    });
  };

  transformMessages = (messages: Array<IMessage>) => {
    return messages.map((message, index) => {
      const isMessageSaving = message.isMessageSaving || false;
      const isMessageError = message.isMessageError || false;

      let authorName = message.authorName || message.author;
      if (message.isSendToStudent && message.messageFrom === 'uni' && this.props.isSamlEnabled) {
        authorName = authorName?.split(' ')[0];
      }

      // const isSystemMessage =
      //   (message.messageType === 'log' || message.messageType === 'welcome') && message.isSendToStudent;

      const messageStyle = () => {
        if (message.messageFrom === 'student') {
          return 'outgoing';
        } else if (message.messageType === 'log' || message.messageType === 'welcome') {
          return 'system';
        } else {
          return 'incoming';
        }
      };

      const getAuthorRole = () => {
        switch (message.messageFrom) {
          case 'uni':
            return 'uni';

          case 'enroly':
            return 'enroly';

          case 'student':
            return 'student';

          case 'agent':
            return 'agent';

          case 'partner':
          case 'immigrationProvider':
            return 'partner';

          case 'system':
            return 'system';

          default:
            return undefined;
        }
      };

      return (
        <>
          <Message
            key={`message-field-item-${message.messageId}-${index}`}
            messageType={message.messageType}
            messageFrom={message.messageFrom || ''}
            messageTo={message.messageTo || ''}
            message={message.message}
            author={authorName}
            createdAt={message.createdAt}
            isSendToStudent={message.isSendToStudent}
            messageStyle={messageStyle()}
            authorStyle={getAuthorRole()}
            isMessageSaving={isMessageSaving}
            isMessageError={isMessageError}
          />
        </>
      );
    });
  };

  handleAutomatedResponse = (messages: Array<IMessage>) => {
    const index = findLastIndex(messages, message => message.messageType === 'log');
    const currentTime = moment(new Date());
    const currentTimeMinus48Hours = moment(currentTime).subtract('48', 'hours');
    let shouldNotifyUserOfResponseTime = true;

    if (index > -1) {
      shouldNotifyUserOfResponseTime = !moment(messages[index].createdAt).isBetween(
        currentTimeMinus48Hours,
        currentTime
      );
    }

    this.setState({
      shouldNotifyUserOfResponseTime
    });
  };

  submitMessage = () => {
    if (!this.state.message) {
      return;
    }
    const messageType = this.state.tabIndexes.get(this.state.tabIndex) || '';
    const newMessage: IMessage = {
      message: this.state.message,
      createdAt: Date.now(),
      isSendToStudent: this.state.isSendToStudent,
      messageType,
      author: this.props.userName || 'Anonymous',
      isMessageSaving: true,
      isMessageError: false,
      messageFrom: 'student',
      messageTo: this.getTo(messageType),
      read: true,
      messageId: `${Date.now()}`
    };

    this.props.onSubmitMessage(newMessage);

    const messages = this.state.messages;
    messages.push(newMessage);
    this.setState({
      messages,
      message: '',
      isSendToStudent: true,
      isVisibleTooltip: false,
      tooltipIndex: null
    });

    if (this.state.shouldNotifyUserOfResponseTime && messageType === 'student') {
      this.sendAutomatedReply(messages, this.props.studentAutomatedChatReply);
    }
  };

  getTo = (messageType: string) => {
    switch (messageType) {
      case 'secure':
        return 'uni';
      case 'agent':
      case 'agentStudent':
        return 'agent';
      case 'immigrationProvider':
        return 'immigrationProvider';
      default:
        return 'uni';
    }
  };

  sendAutomatedReply = (messages: Array<IMessage>, studentAutomatedChatReply: string) => {
    const newMessage: IMessage = {
      message: studentAutomatedChatReply,
      createdAt: Date.now(),
      isSendToStudent: this.state.isSendToStudent,
      messageType: 'log',
      author: 'System',
      messageFrom: 'system',
      messageTo: 'student',
      read: true, // shows immediately after initial message sends
      messageId: `${Date.now()}`
    };

    this.props.onSubmitMessage(newMessage);
    messages.push(newMessage);
    this.setState({
      messages,
      message: '',
      isSendToStudent: true
    });
  };

  updateUnreads = (index: number) => {
    const tab = this.state.tabIndexes.get(index);
    switch (tab) {
      case 'student':
      default:
        if (this.state.unreadMessagesCountFromUni > 0) {
          this.setState({
            unreadMessagesCountFromUni: 0
          });
          this.props.markMessagesRead(
            this.getUnreadMessagesFromUni(this.state.messages).map(message => message.messageId!)
          );
        }
        break;
      case 'agent':
        if (this.state.unreadMessagesAgentToStudent > 0) {
          this.setState({
            unreadMessagesAgentToStudent: 0
          });
          this.props.markMessagesRead(
            this.getUnreadMessagesFromAgent(this.state.messages).map(message => message.messageId!)
          );
        }
        break;
      case 'secure':
        if (this.state.unreadMessagesStudentSecure > 0) {
          this.setState({
            unreadMessagesStudentSecure: 0
          });
          this.props.markMessagesRead(
            this.getUnreadMessagesFromSecure(this.state.messages).map(message => message.messageId!)
          );
        }
        break;
      case 'immigrationProvider':
        if (this.state.unreadMessagesImmigrationProviderToStudent > 0) {
          this.setState({
            unreadMessagesImmigrationProviderToStudent: 0
          });
          this.props.markMessagesRead(
            this.getUnreadMessagesFromImmigrationProvider(this.state.messages).map(message => message.messageId!)
          );
        }
        break;
    }
  };

  filterMessagesByType = (messages: Array<IMessage>, tabType: string | undefined): Array<IMessage> => {
    switch (tabType) {
      case 'agent':
        return this.filterMessagesByAgent(messages);
      case 'secure':
        return this.filterMessagesBySecure(messages);
      case 'immigrationProvider':
        return this.filterMessagesByImmigrationProvider(messages);
      default:
        return this.filterMessagesByUni(messages);
    }
  };

  filterMessagesByAgent = (messages: Array<IMessage>): Array<IMessage> => {
    return messages.filter(message => {
      return (
        message.messageType === 'agent' ||
        message.messageType === 'agentStudent' ||
        (message.messageType === 'welcome' && message.channel === 'agent' && message.messageTo === 'student')
      );
    });
  };

  filterMessagesByImmigrationProvider = (messages: Array<IMessage>): Array<IMessage> => {
    return messages.filter(message => {
      return message.messageType === 'immigrationProvider';
    });
  };

  filterMessagesBySecure = (messages: Array<IMessage>): Array<IMessage> => {
    return messages.filter(message => {
      return (
        message.messageType === 'secure' ||
        (message.messageType === 'welcome' && message.channel === 'secure' && message.messageTo === 'student')
      );
    });
  };

  filterMessagesByUni = (messages: Array<IMessage>): Array<IMessage> => {
    return messages.filter(message => {
      return (
        message.messageType !== 'secure' &&
        message.messageType !== 'agent' &&
        message.messageType !== 'agentStudent' &&
        message.messageType !== 'immigrationProvider' &&
        message.messageType !== 'agentUni'
      );
    });
  };

  render() {
    const {
      unreadMessagesCountFromUni,
      unreadMessagesAgentToStudent,
      unreadMessagesStudentSecure,
      unreadMessagesImmigrationProviderToStudent,
      visaAssistance
    } = this.state;

    const totalUnreadCount =
      unreadMessagesCountFromUni +
      unreadMessagesAgentToStudent +
      unreadMessagesStudentSecure +
      unreadMessagesImmigrationProviderToStudent;

    const getTabPanel = () => {
      const getNode = (tabIndex: number) => {
        return (
          <TabPanel
            key={tabIndex}
            p={0}
            overflow="auto"
            display="flex"
            flexDirection="column-reverse"
            // 100vh - Tabs - Footer - Message Button - Button Spacing - Navbar - extra space
            height="calc(100vh - 64px - 48px - 64px - 24px - 60px - 64px)"
            maxHeight="40rem"
            _focusVisible={{ boxShadow: 'outline' }}
          >
            <Box>
              {this.state.messages && this.state.messages.length > 0
                ? this.transformMessages(
                    this.filterMessagesByType(this.state.messages, this.state.tabIndexes.get(tabIndex))
                  )
                : null}
            </Box>
          </TabPanel>
        );
      };

      // render all views
      // (this.state.tabIndexes.size = 3) => [0, 1, 2]
      const nodes = [...Array(this.state.tabIndexes.size).keys()].map(getNode);

      return <TabPanels>{nodes}</TabPanels>;
    };

    const fixedSpacing = { base: 3, lg: 6 };

    return (
      <>
        <Box position="fixed" bottom={fixedSpacing} right={fixedSpacing}>
          <Popover
            placement="top-end"
            gutter={12}
            arrowSize={12}
            flip={false}
            isLazy={true}
            lazyBehavior="keepMounted"
            strategy="fixed"
            onOpen={() => {
              this.onClickMinimisedInbox();
              this.updateUnreads(0);
            }}
          >
            <MessagesButton totalUnreadCount={totalUnreadCount} />

            <Portal>
              <PopoverContent width={{ base: 'xs', md: 'md' }}>
                <PopoverArrow />
                <PopoverCloseButton />

                <Tabs
                  index={this.state.tabIndex}
                  onChange={index => {
                    this.updateUnreads(index);
                    this.setState({ tabIndex: index });
                  }}
                >
                  <PopoverHeader p={0}>
                    <TabList height={12} borderColor="chakra-border-color">
                      <Tab key="Uni" fontSize="sm" position="relative">
                        University
                        <UnreadBadge count={unreadMessagesCountFromUni} />
                      </Tab>

                      {this.state.hasAgentMessages && (
                        <Tab key="visa" fontSize="sm" position="relative">
                          {visaAssistance ? 'Enroly' : 'Agent'}
                          <UnreadBadge count={unreadMessagesAgentToStudent} />
                        </Tab>
                      )}

                      {this.state.hasSecureMessages && (
                        <Tab key="uniPrivate" fontSize="sm" position="relative">
                          Uni (private)
                          <UnreadBadge count={unreadMessagesStudentSecure} />
                        </Tab>
                      )}

                      {this.state.hasImmigrationProviders && (
                        <Tab key="visaSupport" fontSize="sm" position="relative">
                          Visa support
                          <UnreadBadge count={unreadMessagesImmigrationProviderToStudent} />
                        </Tab>
                      )}
                    </TabList>
                  </PopoverHeader>

                  <PopoverBody p={0}>{getTabPanel()}</PopoverBody>
                </Tabs>

                <PopoverFooter p={0} borderTop="2px solid" borderTopColor="chakra-border-color">
                  <Flex alignItems="end" gap={1}>
                    <Textarea
                      aria-label="Your message"
                      value={this.state.message}
                      onChange={(event: any) => this.setState({ message: event.currentTarget.value })}
                      placeholder={'Type your message here...'}
                      rows={2}
                      minHeight={12}
                      maxHeight="10rem"
                      variant="unstyled"
                      px={4}
                      py={2}
                    />

                    <IconButton
                      aria-label="Send message"
                      id="submit-log-button"
                      onClick={this.submitMessage}
                      isDisabled={!this.state.message}
                      icon={<FontAwesomeIcon icon={faPaperPlane} />}
                      variant="solid"
                      size="lg"
                      isRound
                      mx={2}
                      my={2}
                    />
                  </Flex>
                </PopoverFooter>
              </PopoverContent>
            </Portal>
          </Popover>
        </Box>
      </>
    );
  }
}
